import React, { useRef, useState } from "react";
import { Box, Collapse } from "@mui/material";
import dayjs from "dayjs";
import FilterBoxschema from "@/Config/Export/Dashboard/FilterBox";
import FormGenerator from "@/Utils/FormGenerator/FormGenerator";
import { useUser } from "@/Context/UserContext";

export default function FilterBar({
  onSearch,
  setIsFilterApplied,
  setFilteredData,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false); // ✅ default collapsed
  const [filledData, setFilledData] = useState({});
  const [formKey, setFormKey] = useState(0);
  const [filterButtonLabel, setFilterButtonLabel] =
    useState("Advanced Filters");

  const { formData } = useUser();
  const formRef = useRef();
  const advRef1 = useRef();
  const advRef2 = useRef();
  const advRef3 = useRef();
  const [jobPlaceholder, setJobPlaceholder] = useState("Enter Job No");

  // When task changes
  const handleTaskChange = (e) => {
    const selectedValue = e.target.value;
    const selected = taskTypeOptions.find((opt) => opt.value === selectedValue);
    const newPlaceholder = selected ? `Enter ${selected.label}` : "Enter Job No";
    setJobPlaceholder(newPlaceholder);
  };
  
  // ✅ Reset all filters
  const handleReset = async () => {
    await Promise.all([
      formRef.current?.reset?.(),
      advRef1.current?.reset?.(),
      advRef2.current?.reset?.(),
      advRef3.current?.reset?.(),
    ]);
    setFilledData({});
    setIsFilterApplied?.(false);
    onSearch(null);
    setShowAdvanced(false); // ✅ collapse after reset
    setFilterButtonLabel("Advanced Filters"); // ✅ reset label
    setFormKey((prev) => prev + 1);
  };

  // ✅ Common search
  const handleCommonSearch = (name, value) => {
    if (name === "CommonSearch") {
      let searchText = "";

      if (value?.target?.value !== undefined) {
        searchText = value.target.value.trim();
      } else if (typeof value === "string") {
        searchText = value.trim();
      } else if (value?.label) {
        searchText = value.label.trim();
      } else if (value?.value) {
        searchText = String(value.value).trim();
      }

      setFilledData((prev) => ({
        ...prev,
        CommonSearch: searchText,
      }));

      if (searchText.length === 0) {
        setFilteredData?.(null);
        setIsFilterApplied?.(false);
        onSearch?.({ filter: {} });
      } else if (searchText.length >= 3) {
        onSearch?.({ filter: { CommonSearch: searchText } });
      }
    }
  };

  // ✅ Toggle advanced filters
  const handleToggleAdvanced = () => {
    if (filterButtonLabel === "Clear Filters") {
      // 🔹 if button says Clear Filters → just reset
      handleReset();
    } else {
      // 🔹 otherwise open advanced filters
      setShowAdvanced(true);
    }
  };

  // ✅ Handle search click
  const handleSearchClick = async () => {
    const mainData = (await formRef.current?.submit()) || {};
    const adv1 = (await advRef1.current?.submit()) || {};
    const adv2 = (await advRef2.current?.submit()) || {};
    const adv3 = (await advRef3.current?.submit()) || {};

    const combinedData = {
      ...mainData,
      ...adv1,
      ...adv2,
      ...adv3,
      ...filledData,
    };

    setFilledData(combinedData);

    const dynamicFilter =
      combinedData?.task && combinedData?.jobNumber
        ? { key: combinedData.task, value: combinedData.jobNumber }
        : undefined;

    const payload = {
      filter: {
        CommonSearch: combinedData?.CommonSearch || "",
        exportTransportMode: combinedData?.TransportMode || "",
        exporterJobFillingMode: combinedData?.JobFillingMode || "",
        exporterName:
          combinedData?.exporterName?.id || combinedData?.exporterName || "",
        exportCustomHouse: combinedData?.CustomHouse || "",
        exportSBType: combinedData?.SBType || "",
        exportLoadingPort:
          combinedData?.LoadingPort?.id || combinedData?.LoadingPort || "",
        exportDestinationPort:
          combinedData?.DestinationPort?.id ||
          combinedData?.DestinationPort ||
          "",
        isActive: combinedData?.Active || "",
        Status: combinedData?.Status || "",
        dateRange:
          combinedData?.dateRange?.[0] && combinedData?.dateRange?.[1]
            ? {
                from: dayjs(combinedData.dateRange[0]).format("YYYY-MM-DD"),
                to: dayjs(combinedData.dateRange[1]).format("YYYY-MM-DD"),
              }
            : undefined,
        dynamicFilter,
      },
    };

    onSearch(payload);
    setIsFilterApplied?.(true);

    // ✅ collapse after search and update label
    setShowAdvanced(false);
    setFilterButtonLabel("Clear Filters");
  };

  // ✅ Main schema – only row1’s row1
  const updatedSchema = {
    ...FilterBoxschema,
    row1: FilterBoxschema.row1.map((col) => ({
      ...col,
      rows: {
        row1: col.rows?.row1?.map((field) =>
          field.name === "Advanced Filters"
            ? { ...field, label: filterButtonLabel }
            : field
        ),
      },
    })),
  };


  return (
    <Box
      sx={{
        backgroundColor: "var(--gray-0)",
        border: "1px solid var(--brand-200)",
        borderRadius: 2,
        py: 1,
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleSearchClick();
        }
      }}
    >
      {/* Main Filters */}
      <FormGenerator
        key={`main-${formKey}`}
        ref={formRef} 
        showLoader={false}
        schema={updatedSchema}
        onChange={handleCommonSearch}
        onSubmit={({ action, values }) => {
          if (values) setFilledData(values);
          if (action === "toggleAdvanced") handleToggleAdvanced();
          if (action === "search") handleSearchClick();
          if (action === "closeAdvanced") handleReset();
        }}
      />

      {/* Advanced Filters */}
      {/* ✅ Advanced Filters */}
      <Collapse in={showAdvanced}>
        <Box>
          <Box sx={{ px: 2 }}>
            <FormGenerator
              key={`adv1-${formKey}`}
              showLoader={false}
              ref={advRef1}
              schema={{
                row1: FilterBoxschema.row1.map((col) => ({
                  ...col,
                  rows: {
                    row2: col.rows?.row2 || [],
                    row3: col.rows?.row3 || [],
                    row4: col.rows?.row4 || [],
                    // row5: col.rows?.row5 || [],
                  },
                })),
              }}
              onChange={handleCommonSearch}
              onSubmit={({ action }) => {
                if (action === "toggleAdvanced") handleToggleAdvanced();
                if (action === "search") handleSearchClick();
                if (action === "closeAdvanced") handleReset();
              }}
            />
          </Box>

          <Box>
            <FormGenerator
              key={`adv2-${formKey}`}
              showLoader={false}
              ref={advRef2}
              schema={{
                row1: FilterBoxschema.row1.map((col) => ({
                  ...col,
                  rows: { row5: col.rows?.row5 || [] },
                })),
              }}
              onChange={handleCommonSearch}
              onSubmit={({ action }) => {
                if (action === "toggleAdvanced") handleToggleAdvanced();
                if (action === "search") handleSearchClick();
                if (action === "closeAdvanced") handleReset();
              }}
            />
          </Box>

          <Box sx={{ px: 2 }}>
            <FormGenerator
              key={`adv3-${formKey}`}
              showLoader={false}
              ref={advRef3}
              schema={{
                row1: FilterBoxschema.row1.map((col) => ({
                  ...col,
                  rows: { row6: col.rows?.row6 || [] },
                })),
              }}
              onChange={handleCommonSearch}
              onSubmit={({ action }) => {
                if (action === "toggleAdvanced") handleToggleAdvanced();
                if (action === "search") handleSearchClick();
                if (action === "closeAdvanced") handleReset();
              }}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
